import React from 'react';
import autoBind from 'react-autobind';
import Localization from "../../Localization.jsx";
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  Select,
  Alert,
  Loader,
} from '@mantine/core';
import { IconUserCog, IconAlertTriangle } from '@tabler/icons-react';

/**
 * ChangeOwnerDialog — re-author one locale (PATCH /api/epack/{id}/owner).
 *
 * SCHOOL ADMIN ONLY, and the card only offers it for locales of the admin's
 * own school; the server repeats both tests (require_admin + same school) and
 * additionally insists the new owner is a live STAFF account of the LOCALE's
 * school — so the roster here is filtered the same way (role >= 0, not
 * soft-deleted), and a 400 from the server is shown as that sentence rather
 * than a generic failure.
 *
 * The locale never changes school here. What changes is whose 「我的 LOCALE」
 * tab it appears on, and who may edit it without being an admin.
 *
 * Props: root, opened, locale (the list row: id, name, school_id, user_id,
 * display_name, username), onClose(), onChanged() — the parent refreshes its
 * list on the latter.
 */
class ChangeOwnerDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      users: [],
      usersLoading: false,
      usersFailed: false,
      userId: null,
      submitting: false,
      error: "",
    };
    autoBind(this);
  }

  componentDidUpdate(prevProps) {
    if (this.props.opened && !prevProps.opened) {
      this.setState({ users: [], userId: null, submitting: false, error: "" });
      this.fetchUsers();
    }
  }

  fetchUsers() {
    const root = this.props.root;
    const locale = this.props.locale;
    if (!root || !root.refs || !root.refs.API || !locale) {
      return;
    }
    this.setState({ usersLoading: true, usersFailed: false });
    root.refs.API.request(
      "get_user_data_by_school_id",
      { school_id: locale.school_id },
      this.fetchUsersOK,
      this.fetchUsersFailed
    );
  }

  fetchUsersOK(res) {
    const rows = res && res.body && Array.isArray(res.body.result) ? res.body.result : [];
    const currentId = this.props.locale ? String(this.props.locale.user_id) : "";
    // Staff only (a learner is provisioned at a negative role), never a
    // soft-deleted tombstone, never the current owner (a no-op the server
    // would answer changed:false anyway).
    const users = rows
      .filter((u) => u && parseInt(u.role, 10) >= 0
        && !String(u.username || '').startsWith('Deleted-')
        && String(u.id) !== currentId)
      .map((u) => ({
        value: String(u.id),
        label: (u.display_name ? u.display_name + ' (' + u.username + ')' : u.username) || ('#' + u.id),
      }));
    this.setState({ usersLoading: false, users: users });
  }

  fetchUsersFailed(err) {
    console.log("get_user_data_by_school_id failed", err);
    this.setState({ usersLoading: false, usersFailed: true });
  }

  handleConfirm() {
    const root = this.props.root;
    const locale = this.props.locale;
    if (!root || !locale || !this.state.userId || this.state.submitting) {
      return;
    }
    this.setState({ submitting: true, error: "" });
    root.refs.API.request(
      "change_epack_owner",
      { epack_id: locale.id, user_id: parseInt(this.state.userId, 10) },
      this.changeOK,
      this.changeFailed
    );
  }

  changeOK() {
    this.setState({ submitting: false });
    if (this.props.root && this.props.root.addNotification) {
      this.props.root.addNotification(Localization.strings.CHANGE_OWNER_DONE || 'Owner changed', true);
    }
    if (this.props.onChanged) {
      this.props.onChanged();
    }
    if (this.props.onClose) {
      this.props.onClose();
    }
  }

  changeFailed(err) {
    console.log("change_epack_owner failed", err);
    const remarks = err && err.body && err.body.remarks;
    const key = remarks === 'invalid_owner' ? 'CHANGE_OWNER_INVALID' : 'CHANGE_OWNER_FAILED';
    this.setState({
      submitting: false,
      error: Localization.strings[key] || 'Could not change the owner',
    });
  }

  render() {
    const locale = this.props.locale || {};
    const currentOwner = locale.display_name || locale.username || (locale.user_id ? '#' + locale.user_id : '-');
    const s = Localization.strings;
    return (
      <Modal
        opened={!!this.props.opened}
        onClose={this.props.onClose}
        closeOnClickOutside={!this.state.submitting}
        closeOnEscape={!this.state.submitting}
        title={
          <Group gap={8}>
            <IconUserCog size={20} />
            <Text fw={600}>{s.CHANGE_OWNER_TITLE || 'Change LOCALE owner'}</Text>
          </Group>
        }
        size="md"
        radius="md"
        centered
        zIndex={2100}
      >
        <Stack gap="sm">
          <Text fw={600}>{locale.name || ''}</Text>
          <Text size="sm">
            {(s.CHANGE_OWNER_CURRENT || 'Current owner') + ': '}
            <Text component="span" fw={600}>{currentOwner}</Text>
          </Text>
          {this.state.usersLoading ? (
            <Group gap={8}><Loader size="sm" /><Text size="sm" c="dimmed">{s.LOADING || 'Loading…'}</Text></Group>
          ) : this.state.usersFailed ? (
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>{s.CHANGE_OWNER_FAILED || 'Could not load the staff list'}</Alert>
          ) : (
            <Select
              label={s.CHANGE_OWNER_NEW || 'New owner'}
              placeholder={this.state.users.length ? (s.CHANGE_OWNER_NEW || 'New owner') : (s.CHANGE_OWNER_NO_USERS || 'No other staff account in this school')}
              data={this.state.users}
              value={this.state.userId}
              onChange={(value) => this.setState({ userId: value, error: "" })}
              searchable
              nothingFoundMessage={s.CHANGE_OWNER_NO_USERS || 'No other staff account in this school'}
              disabled={!this.state.users.length || this.state.submitting}
              comboboxProps={{ zIndex: 2200 }}
            />
          )}
          <Text size="xs" c="dimmed">{s.CHANGE_OWNER_HINT || 'Only staff accounts of this school can be chosen. The locale stays in this school.'}</Text>
          {this.state.error ? (
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>{this.state.error}</Alert>
          ) : null}
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={this.props.onClose} disabled={this.state.submitting}>
              {s.CANCEL || 'Cancel'}
            </Button>
            <Button onClick={this.handleConfirm} loading={this.state.submitting} disabled={!this.state.userId}>
              {s.CHANGE_OWNER_CONFIRM || 'Change'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    );
  }
}

export default ChangeOwnerDialog;
